/**
 * System Messages → Message Center bridge
 *
 * Messages are no longer rendered as auto-dismissing toasts. Instead, every
 * message path feeds a hidden in-DOM "message store" (#flash-messages): the
 * server-side systemmessages fetch (below) and various controllers that call
 * createFlashMessage(). The `message-center` Stimulus controller observes that
 * store, ingests each node, and renders a docked status bar + expandable log.
 *
 * Keeping #flash-messages and the createFlashMessage()/renderSystemMessages()
 * globals means existing callers (jstree / acl-selector controllers, editor
 * forwarding, layout bootstrap) keep working unchanged.
 */
(function () {
    'use strict';

    function getStore() {
        return document.getElementById('flash-messages');
    }

    /**
     * Build a lightweight store node carrying one message. The message-center
     * controller reads these data-* attributes, renders the message, then removes
     * the node. Returned (not appended) so callers that do
     * `store.appendChild(createFlashMessage(...))` keep working.
     */
    function createFlashMessage(msg) {
        const node = document.createElement('div');
        node.className = 'message-store__item';
        node.dataset.type = (msg && msg.type) ? String(msg.type) : 'notice';
        node.dataset.header = (msg && msg.header) ? String(msg.header) : '';
        node.dataset.body = (msg && msg.body != null) ? String(msg.body) : '';
        node.dataset.sticky = (msg && msg.sticky === true) ? '1' : '0';
        return node;
    }

    /**
     * Fetch server-side system messages and push them into the store so the
     * message center renders them — including success/`ok` confirmations (e.g.
     * "Saved" flashed after a redirect). The lightweight topbar "Saved" pulse is
     * driven separately by the inline-autosave controllers (tree / ACL / editor),
     * which dispatch `status:saved` themselves and do not go through this fetch.
     */
    function renderSystemMessages() {
        const store = getStore();
        if (!store) return;

        const controller = new AbortController();
        const abortForNavigation = () => controller.abort();
        window.addEventListener('pagehide', abortForNavigation, { once: true });

        fetch('/?context=systemmessages&event=renderSystemMessages', { signal: controller.signal })
            .then(response => response.json())
            .then(payload => {
                if (!payload || payload.ok !== true) {
                    console.error('Failed to load system messages:', payload);
                    return;
                }

                const data = payload.data || [];
                // Backend returns an object keyed by hash; normalise to an array.
                const messages = Array.isArray(data) ? data : Object.values(data);

                messages.forEach(msg => store.appendChild(createFlashMessage(msg)));
            })
            .catch(err => {
                if (!controller.signal.aborted) {
                    console.error('Failed to load system messages:', err);
                }
            })
            .finally(() => window.removeEventListener('pagehide', abortForNavigation));
    }

    // Export to global scope
    // Under its own name too (what the CMS's inline callers look for first) -- see so-admin's copy.
    window.radaptorSystemMessages = { render: renderSystemMessages, createFlashMessage };
    window.renderSystemMessages = renderSystemMessages;
    window.createFlashMessage = createFlashMessage;
})();
